import React, { useState, useEffect } from "react";
import { MessageCircle, Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-16 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-4 py-2 text-sm font-semibold text-white transition-colors hover:text-white/90">
                {item.label}
                <span className="absolute bottom-0 left-0 h-[2px] w-full origin-left scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return null;
  return <nav className="flex flex-col space-y-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-semibold text-foreground hover:text-primary transition-colors" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 dark border-b border-white/10", isScrolled ? "bg-background/80 backdrop-blur-md shadow-sm" : "bg-background")}>
      <div className="container mx-auto px-6 h-24 flex items-center justify-between">
        {}
        <Link to="/HomePage#" className="flex-shrink-0">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557890/img/image_tqykgdrm.png" alt="Call Green Haven Property Maintenance" className="object-contain !w-[100px] !h-[100px]" />
        </Link>

        {}
        <div className="hidden md:flex items-center justify-center flex-1 px-8">
          <MenuProvider menu_id="52">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex items-center gap-4">
          <Button variant="ghost" size="icon" className="text-white hover:text-primary hover:bg-white/5 rounded-full" aria-label="Chat">
            <MessageCircle className="h-5 w-5" />
          </Button>
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-md">
            <Link to="#estimate">Request Estimate</Link>
          </Button>
        </div>

        {}
        <div className="flex md:hidden items-center gap-4">
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-background border-b border-white/10 shadow-xl animate-in slide-in-from-top-5 duration-200">
          <div className="container mx-auto px-4 py-6 flex flex-col gap-6">
            <MenuProvider menu_id="52">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            
            <div className="h-px w-full bg-white/10" />
            
            <div className="flex flex-col gap-4">
              <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                <Link to="#estimate" onClick={() => setIsMobileMenuOpen(false)}>
                  Request Estimate
                </Link>
              </Button>
              <Button variant="outline" className="w-full justify-center gap-2 border-white/20 text-white hover:bg-white/5 hover:text-primary" onClick={() => setIsMobileMenuOpen(false)}>
                <MessageCircle className="h-4 w-4" />
                Chat with us
              </Button>
            </div>
          </div>
        </div>}
    </header>;
}